import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ArrowDown, AlertCircle, MapPin, CalendarClock, Droplets, Search,
  Smartphone, Car, Store, Wrench, Star, Building2, Users, Layers, ShieldCheck, HeartHandshake,
  TrendingUp, IndianRupee, Rocket, Landmark, Gauge, Repeat, Check, X, Zap,
  ClipboardList, MessageSquare, CreditCard, CalendarCheck, Database,
  UserCheck, Globe2, Plug, ArrowUpRight,
} from "lucide-react";
import { Reveal, SectionHead, Btn } from "../components/ui-bits";
import AggregatorMapSection, { WhyAggregator, CentreCTA } from "../sections/AggregatorMap";
import AggregatorBookingModal from "../components/AggregatorBookingModal";
import { useApp } from "../context/AppContext";
import { waLink } from "../lib/api";
import { IMAGES } from "../config/images";

const PROBLEMS = [
  { img: IMAGES.foamWash, text: "Customers struggle to find reliable nearby car-care providers." },
  { img: IMAGES.garage, text: "Existing car-care centres can face idle capacity and inconsistent demand." },
  { img: IMAGES.apartment, text: "Traditional vehicle-cleaning processes can involve inefficient water usage and inconsistent service standards." },
];

const MODEL_POINTS = [
  "Customers discover nearby service providers",
  "Customers can view available services and booking slots",
  "Digital booking and scheduling",
  "Service providers receive customer demand",
  "CARZA can standardize service processes through SOPs",
  "Customer and service activity can be tracked digitally",
];

const FLOW = [
  { icon: Users, label: "Customer" },
  { icon: Layers, label: "CARZA Platform" },
  { icon: Store, label: "Nearby Service Provider" },
  { icon: Wrench, label: "Car Care Service" },
  { icon: Star, label: "Customer Feedback" },
];

const STEPS = [
  { icon: Smartphone, title: "Customer Opens CARZA", desc: "Customer searches for available car-care services." },
  { icon: MapPin, title: "Finds Nearby Service", desc: "CARZA displays relevant service providers / service options based on location." },
  { icon: CalendarClock, title: "Books a Slot", desc: "Customer selects the centre, service and an available slot — and pays the centre directly after the wash." },
  { icon: Car, title: "Service is Delivered", desc: "The partner provides the service according to CARZA's defined service process." },
];

const PILLARS4 = [
  { icon: TrendingUp, title: "Demand", desc: "Customer acquisition and booking demand." },
  { icon: CalendarClock, title: "Scheduling", desc: "Digital appointment and service-slot management." },
  { icon: ShieldCheck, title: "Service Standards", desc: "Defined processes, training and quality controls." },
  { icon: HeartHandshake, title: "Customer Relationship", desc: "Digital booking history, feedback and recurring service opportunities." },
];

const IMPACT = [
  { icon: Gauge, title: "Higher Utilization", desc: "Help participating service providers access additional customer demand and improve utilization." },
  { icon: IndianRupee, title: "New Revenue Channel", desc: "Enable service providers to access incremental bookings through the CARZA platform." },
  { icon: Rocket, title: "Faster Market Expansion", desc: "Build city-level supply through a distributed service-provider network." },
  { icon: Landmark, title: "Lower Infrastructure Dependency", desc: "Expand the network without requiring CARZA to build a physical car-wash centre for every location." },
];

const PROVIDER_VALUE = [
  { icon: Users, title: "More Customer Demand", desc: "Access customers beyond walk-in traffic." },
  { icon: Gauge, title: "Better Utilization", desc: "Fill available service capacity with scheduled bookings." },
  { icon: Smartphone, title: "Digital Bookings", desc: "Reduce dependency on manual calls and walk-ins." },
  { icon: MapPin, title: "Customer Discovery", desc: "Become discoverable to customers searching for car care nearby." },
  { icon: ClipboardList, title: "Standardized Operations", desc: "Access CARZA's operating processes and service standards." },
  { icon: Repeat, title: "Recurring Opportunities", desc: "Build relationships with customers through repeat service." },
];

const CUSTOMER_VALUE = [
  { icon: MapPin, title: "Find Car Care Nearby" },
  { icon: CalendarClock, title: "Book a Convenient Slot" },
  { icon: Layers, title: "Compare Service Options" },
  { icon: ShieldCheck, title: "Get Professional Service" },
  { icon: Smartphone, title: "Manage Bookings Digitally" },
  { icon: Repeat, title: "Build a Regular Car-Care Routine" },
];

const ORBIT = [
  { icon: CalendarCheck, label: "Booking" },
  { icon: CreditCard, label: "Direct Vendor Payment" },
  { icon: CalendarClock, label: "Scheduling" },
  { icon: ShieldCheck, label: "Service Standards" },
  { icon: Database, label: "Customer Data" },
  { icon: MessageSquare, label: "Feedback" },
  { icon: UserCheck, label: "Partner Management" },
];

const ECOSYSTEM_CHAIN = [
  { icon: Car, label: "Car Owners" },
  { icon: Layers, label: "CARZA Platform" },
  { icon: UserCheck, label: "Service Partners" },
  { icon: Store, label: "Car Care Centres / Mobile Partners" },
  { icon: Building2, label: "B2B Clients" },
];

const INVESTOR_PILLARS = [
  { icon: Layers, title: "Asset-Light Network" },
  { icon: Store, title: "Distributed Service Capacity" },
  { icon: Repeat, title: "Recurring Customer Relationships" },
  { icon: Zap, title: "Technology + Standardized Operations" },
];

const CITY_POS = {
  "Delhi NCR": { x: 132, y: 84 },
  Noida: { x: 140, y: 94 },
  Mumbai: { x: 88, y: 176 },
  Bengaluru: { x: 146, y: 252 },
};

const NEARBY_LIVE = false; // flip to true when CARZA Nearby launches

function NearbyComingSoon() {
  return (
    <main className="relative min-h-[100svh] overflow-hidden bg-lavender-2 pb-20" data-testid="nearby-coming-soon">
      <div className="dot-grid-light pointer-events-none absolute inset-0" />
      <div className="pointer-events-none absolute -right-24 top-10 h-80 w-80 rounded-full" style={{ background: "radial-gradient(circle, rgba(109,61,245,0.16), transparent 70%)" }} />
      <div className="container-x relative flex min-h-[100svh] flex-col items-center justify-center pt-16 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-3xl bg-carza-dim text-carza">
          <MapPin size={30} />
        </span>
        <span className="mt-6 rounded-full border border-carza/30 bg-carza-dim px-5 py-1.5 font-mono text-[11px] uppercase tracking-[0.25em] text-carza" data-testid="nearby-soon-badge">
          Coming Soon
        </span>
        <h1 className="mt-6 max-w-2xl font-display text-4xl font-bold leading-[1.05] tracking-tight text-nighttext sm:text-5xl lg:text-6xl">
          CARZA Nearby is <span className="text-carza">Almost Here.</span>
        </h1>
        <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-[#5c5470] sm:text-base">
          One map, every trusted car wash near you — bookable in under a minute. We're polishing the last detail before we open it up.
        </p>
        <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
          <a href={waLink("Hi CARZA — notify me when CARZA Nearby launches.")} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-full bg-carza px-7 py-3.5 text-[14px] font-semibold text-white shadow-[0_18px_40px_-16px_rgba(109,61,245,0.65)] transition-transform hover:-translate-y-0.5" data-testid="nearby-notify-btn">
            Notify Me on WhatsApp
          </a>
          <Link to="/" className="flex items-center gap-2 rounded-full border border-nighttext/15 bg-white px-7 py-3.5 text-[14px] font-semibold text-nighttext transition-transform hover:-translate-y-0.5" data-testid="nearby-home-btn">
            Back to Home
          </Link>
        </div>
        <div className="mt-12 flex flex-wrap items-center justify-center gap-2.5">
          {["Every nearby centre, one map", "Transparent ratings", "80% less water"].map((chip) => (
            <span key={chip} className="flex items-center gap-1.5 rounded-full border border-carza/25 bg-carza-dim/60 px-4 py-1.5 text-[12px] font-semibold text-carza">
              <Droplets size={12} /> {chip}
            </span>
          ))}
        </div>
      </div>
    </main>
  );
}

export default function AggregatorPage() {
  const [bookingCentre, setBookingCentre] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  if (!NEARBY_LIVE) return <NearbyComingSoon />;
  const scrollToCentres = () => {
    const el = document.getElementById("nearby-centres");
    if (el) window.__lenis ? window.__lenis.scrollTo(el, { offset: -76 }) : el.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <main className="bg-lavender-2 pb-20 lg:pb-0" data-testid="aggregator-page">
      <Hero onFind={scrollToCentres} />
      <AggregatorMapSection onSelectCentre={(c) => { setBookingCentre(c); setModalOpen(true); }} />
      <WhyAggregator />
      <CentreCTA />
      <ProblemCard />
      <ModelCard />
      <HowItWorks />
      <OperatingLayer />
      <BusinessImpact />
      <ProviderValue />
      <CustomerValue onFind={scrollToCentres} />
      <WaterEfficient />
      <Ecosystem />
      <Scalability />
      <InvestorView />
      <FinalCTA onFind={scrollToCentres} />
      <AggregatorBookingModal open={modalOpen} centre={bookingCentre} onClose={() => setModalOpen(false)} />
    </main>
  );
}

/* ---------- HERO ---------- */
function Hero({ onFind }) {
  return (
    <header className="relative overflow-hidden pt-[68px]" data-testid="aggregator-hero">
      <div className="absolute inset-0 bg-gradient-to-b from-lavender via-lavender-2 to-lavender-2" />
      <div className="pointer-events-none absolute -right-24 top-10 h-[420px] w-[420px] rounded-full" style={{ background: "radial-gradient(circle, rgba(124,77,255,0.16), transparent 70%)" }} />
      <div className="container-x relative grid grid-cols-1 items-center gap-12 py-14 lg:grid-cols-12 lg:py-20">
        <div className="lg:col-span-7">
          <Reveal>
            <span className="eyebrow eyebrow-dark">CARZA Nearby</span>
          </Reveal>
          <h1 className="mt-5 font-display text-4xl font-bold leading-[1.08] tracking-tight sm:text-5xl lg:text-[3.6rem]" data-testid="aggregator-title">
            <span className="text-nighttext">Find a Car Wash </span>
            <span className="text-carza">Near You.</span>
          </h1>
          <Reveal delay={0.14}>
            <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-[#5c5470] sm:text-base" data-testid="aggregator-hero-sub">
              Discover nearby car washing centres, check available slots, and book a convenient time. Pay the service provider directly after your car wash.
            </p>
          </Reveal>
          <Reveal delay={0.2} className="mt-8 flex flex-wrap gap-4">
            <button onClick={onFind} className="btn btn-primary px-7 py-4 text-[15px]" data-testid="aggregator-hero-book-btn">
              Find a Centre &amp; Book a Slot <ArrowRight size={16} />
            </button>
            <Link to="/partners" className="btn btn-outline-dark px-7 py-4 text-[15px]" data-testid="aggregator-hero-partner-btn">
              Become a CARZA Partner
            </Link>
          </Reveal>
          <Reveal delay={0.26} className="mt-6 flex flex-wrap gap-2">
            {["Find a nearby centre", "Choose a slot", "Pay the centre after your wash"].map((c) => (
              <span key={c} className="flex items-center gap-1.5 rounded-full border border-carza/25 bg-carza-dim/50 px-3.5 py-1.5 text-[11.5px] font-semibold text-carza" data-testid="aggregator-hero-chips">
                <Check size={11} /> {c}
              </span>
            ))}
          </Reveal>
        </div>
        <div className="relative lg:col-span-5">
          <Reveal delay={0.15}>
            <div className="relative mx-auto max-w-[480px]">
              <div className="overflow-hidden rounded-3xl border border-white/60 shadow-[0_40px_80px_-40px_rgba(23,19,31,0.4)]">
                <img src={IMAGES.partnerIndia} alt="A CARZA service partner servicing a car in an Indian residential society" className="aspect-[4/3] w-full object-cover" loading="eager" fetchPriority="high" data-testid="aggregator-hero-image" />
                <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 55%, rgba(38,32,64,0.55))" }} />
                <div className="absolute bottom-4 left-4 flex items-center gap-2.5 rounded-2xl bg-white/90 px-4 py-2.5 backdrop-blur">
                  <TrendingUp size={15} className="text-carza" />
                  <span className="text-[12px] font-semibold text-nighttext">Technology Partners<br /><span className="text-[10px] font-medium text-[#5c5470]">Grow Together</span></span>
                </div>
              </div>
              <div className="absolute -top-6 -right-2 hidden sm:block lg:-right-6">
                <PhoneMock />
              </div>
              <div className="glass float-y absolute -bottom-5 right-4 flex items-center gap-2 rounded-2xl px-4 py-2.5" style={{ animationDelay: "1s" }} data-testid="aggregator-hero-chip">
                <MapPin size={14} className="text-carza-lite" />
                <span className="text-[12px] font-semibold text-paper">Cleaner Cities · Happier Drivers</span>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </header>
  );
}

function PhoneMock() {
  return (
    <div className="w-[220px] rounded-[2.2rem] border-[6px] border-[#1c1526] bg-white shadow-[0_30px_60px_-25px_rgba(23,19,31,0.5)]" data-testid="aggregator-phone-mock">
      <div className="flex items-center justify-between rounded-t-[1.6rem] bg-gradient-to-r from-carza to-carza-lite px-4 pb-2.5 pt-3 text-white">
        <span className="flex items-center gap-1.5 font-display text-[13px] font-bold">🚗 carza</span>
        <span className="flex items-center gap-1 font-mono text-[8px]"><MapPin size={9} /> Delhi NCR</span>
      </div>
      <div className="p-3">
        <div className="flex items-center gap-2 rounded-full border border-black/[0.08] bg-[#f6f4fb] px-3 py-1.5 text-[10px] text-[#8a8399]">
          <Search size={10} /> Find car care near you
        </div>
        <div className="mt-2 flex gap-1.5">
          {["Car Wash", "Interior", "Premium", "EV Care"].map((c) => (
            <span key={c} className={`rounded-full px-2 py-1 text-[8px] font-semibold ${c === "Car Wash" ? "bg-carza text-white" : "bg-[#f0edf9] text-[#5c5470]"}`}>{c}</span>
          ))}
        </div>
        <div className="mt-2.5 flex items-center justify-between">
          <p className="text-[9px] font-bold text-nighttext">Nearby Car Care Centres</p>
          <span className="text-[8px] font-semibold text-carza">View all</span>
        </div>
        <div className="mt-1.5 flex flex-col gap-1.5">
          {[
            { name: "CARZA Verified Centre", area: "Model Town", dist: "1.8 km" },
            { name: "CARZA Mobile Partner", area: "Green Residency", dist: "2.4 km" },
          ].map((p) => (
            <div key={p.name} className="rounded-xl border border-black/[0.06] p-2">
              <div className="flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-carza-dim text-carza"><Store size={12} /></span>
                <div className="flex-1">
                  <p className="text-[9px] font-bold leading-tight text-nighttext">{p.name}</p>
                  <p className="text-[8px] text-[#8a8399]">{p.area} · {p.dist} · Water-efficient</p>
                </div>
              </div>
              <button className="mt-1.5 w-full rounded-full bg-carza py-1 text-[8px] font-bold text-white">Book Now</button>
            </div>
          ))}
        </div>
        <p className="mt-2 text-center font-mono text-[7px] uppercase tracking-widest text-[#b7b2c4]">Sample interface</p>
      </div>
    </div>
  );
}

/* ---------- SECTION 1: PROBLEM ---------- */
function ProblemCard() {
  return (
    <section className="container-x pb-6" data-testid="aggregator-problem">
      <Reveal>
        <div className="rounded-[2rem] bg-white p-7 shadow-[0_20px_50px_-30px_rgba(23,19,31,0.25)] sm:p-10" data-testid="aggregator-problem-card">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-carza-dim text-carza"><AlertCircle size={19} /></span>
            <h2 className="font-display text-2xl font-bold text-nighttext sm:text-3xl">The Problem</h2>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
            {PROBLEMS.map((p, i) => (
              <div key={i} className="flex flex-col gap-3" data-testid={`aggregator-problem-${i}`}>
                <div className="relative h-32 overflow-hidden rounded-2xl">
                  <img src={p.img} alt="" loading="lazy" className="h-full w-full object-cover" />
                  <span className="absolute left-3 top-3 rounded-full bg-nighttext/85 px-2.5 py-1 font-mono text-[9px] text-white">{String(i + 1).padStart(2, "0")}</span>
                </div>
                <p className="text-sm leading-relaxed text-[#3d3455]">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </Reveal>
    </section>
  );
}

/* ---------- SECTION 2: MODEL ---------- */
function ModelCard() {
  return (
    <section className="container-x py-6" data-testid="aggregator-model">
      <Reveal>
        <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-[#241543] via-carza-deep to-[#1b0f33] p-7 shadow-[0_40px_90px_-40px_rgba(109,61,245,0.55)] sm:p-10">
          <div className="dot-grid pointer-events-none absolute inset-0 opacity-70" />
          <div className="relative grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-carza-dim text-carza-lite"><Plug size={19} /></span>
                <h2 className="font-display text-2xl font-bold text-paper sm:text-3xl">The CARZA Nearby Model</h2>
              </div>
              <p className="mt-3 text-[15px] text-mist">Connecting Demand, Service Providers &amp; Technology.</p>

              <div className="mt-8 flex flex-col items-stretch gap-1.5 lg:flex-row lg:items-center">
                {FLOW.map((f, i) => (
                  <div key={f.label} className="flex flex-1 flex-col items-center gap-1.5 lg:flex-row">
                    <motion.div whileHover={{ y: -4 }} className="flex w-full flex-col items-center gap-2 rounded-2xl border border-white/10 bg-white/[0.05] px-3 py-4 text-center lg:w-auto lg:flex-1" data-testid={`model-flow-${i}`}>
                      <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${i === 1 ? "bg-carza text-white" : "bg-carza-dim text-carza-lite"}`}><f.icon size={17} /></span>
                      <span className="text-[11px] font-semibold leading-tight text-paper">{f.label}</span>
                    </motion.div>
                    {i < FLOW.length - 1 && <ArrowRight size={14} className="hidden rotate-90 text-carza-lite lg:block" />}
                    {i < FLOW.length - 1 && <ArrowDown size={13} className="text-carza-lite lg:hidden" />}
                  </div>
                ))}
              </div>

              <div className="mt-8 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                {MODEL_POINTS.map((pt, i) => (
                  <p key={pt} className="flex items-start gap-2.5 text-[13px] leading-relaxed text-paper/90" data-testid={`model-point-${i}`}>
                    <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-carza text-white" style={{ width: 16, height: 16 }}><Check size={9} /></span>
                    {pt}
                  </p>
                ))}
              </div>
            </div>
            <div className="hidden lg:col-span-5 lg:flex lg:justify-center">
              <motion.div initial={{ rotate: 6, y: 20 }} whileInView={{ rotate: 0, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.9 }}>
                <PhoneMock />
              </motion.div>
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

/* ---------- SECTION 3: HOW IT WORKS ---------- */
function HowItWorks() {
  return (
    <section className="container-x py-6" data-testid="aggregator-how">
      <Reveal>
        <div className="rounded-[2rem] bg-white p-7 shadow-[0_20px_50px_-30px_rgba(23,19,31,0.25)] sm:p-10" data-testid="aggregator-how-card">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-carza-dim text-carza"><Layers size={19} /></span>
            <h2 className="font-display text-2xl font-bold text-nighttext sm:text-3xl">How It Works</h2>
          </div>
          <div className="relative mt-9 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4 lg:gap-4">
            <div className="pointer-events-none absolute left-0 right-0 top-8 hidden border-t-2 border-dashed border-carza/20 lg:block" aria-hidden="true" />
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08} className="relative">
                <div className="flex flex-col items-center gap-3 text-center lg:px-2" data-testid={`aggregator-step-${i + 1}`}>
                  <span className="relative z-10 flex h-16 w-16 items-center justify-center rounded-2xl border-2 border-carza/25 bg-lavender-2 text-carza shadow-[0_10px_25px_-12px_rgba(109,61,245,0.5)]">
                    <s.icon size={24} />
                    <span className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-carza font-mono text-[10px] font-bold text-white">{i + 1}</span>
                  </span>
                  <h3 className="font-display text-[15px] font-bold text-nighttext">{s.title}</h3>
                  <p className="text-[13px] leading-relaxed text-[#5c5470]">{s.desc}</p>
                  {i < STEPS.length - 1 && <ArrowRight size={16} className="absolute right-[-10px] top-8 hidden text-carza lg:block" />}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Reveal>
    </section>
  );
}

/* ---------- SECTION 4: OPERATING LAYER ---------- */
function OperatingLayer() {
  return (
    <section className="container-x py-6" data-testid="aggregator-layer">
      <Reveal>
        <div className="rounded-[2rem] border border-carza/20 bg-white p-7 shadow-[0_20px_50px_-30px_rgba(109,61,245,0.35)] sm:p-10" data-testid="aggregator-layer-card">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-carza text-white"><Layers size={19} /></span>
            <h2 className="font-display text-2xl font-bold text-nighttext sm:text-3xl">More Than a Marketplace.</h2>
          </div>
          <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-[#5c5470]">
            CARZA connects demand, scheduling, service standards and customer relationships into one operating layer.
          </p>
          <div className="mt-9 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {PILLARS4.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.06}>
                <div className="card-light flex h-full flex-col gap-4 rounded-3xl p-6" data-testid={`aggregator-layer-${i}`}>
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br from-carza to-carza-lite text-white shadow-[0_12px_25px_-10px_rgba(109,61,245,0.6)]"><p.icon size={20} /></span>
                  <div>
                    <h3 className="font-mono text-[11px] font-bold uppercase tracking-[0.18em] text-nighttext">{p.title}</h3>
                    <p className="mt-2 text-[13px] leading-relaxed text-[#5c5470]">{p.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Reveal>
    </section>
  );
}

/* ---------- SECTION 5: BUSINESS IMPACT ---------- */
function BusinessImpact() {
  return (
    <section className="container-x py-6" data-testid="aggregator-impact">
      <Reveal>
        <div className="rounded-[2rem] bg-white p-7 shadow-[0_20px_50px_-30px_rgba(23,19,31,0.25)] sm:p-10" data-testid="aggregator-impact-card">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-carza-dim text-carza"><TrendingUp size={19} /></span>
            <h2 className="font-display text-2xl font-bold text-nighttext sm:text-3xl">Business Impact</h2>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {IMPACT.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.06}>
                <div className="card-light flex h-full flex-col gap-4 rounded-3xl p-6" data-testid={`aggregator-impact-${i}`}>
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-carza-dim text-carza"><c.icon size={18} /></span>
                  <div>
                    <h3 className="font-display text-[15px] font-bold leading-snug text-nighttext">{c.title}</h3>
                    <p className="mt-2 text-[13px] leading-relaxed text-[#5c5470]">{c.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </Reveal>
    </section>
  );
}

/* ---------- SECTION 6: PROVIDER VALUE ---------- */
function ProviderValue() {
  return (
    <section className="container-x py-6" data-testid="aggregator-provider">
      <Reveal>
        <div className="rounded-[2rem] bg-white p-7 shadow-[0_20px_50px_-30px_rgba(23,19,31,0.25)] sm:p-10">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-carza-dim text-carza"><UserCheck size={19} /></span>
                <h2 className="font-display text-2xl font-bold text-nighttext sm:text-3xl">Why Would a Car-Care Provider Join CARZA?</h2>
              </div>
              <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {PROVIDER_VALUE.map((v, i) => (
                  <Reveal key={v.title} delay={i * 0.05}>
                    <div className="flex items-start gap-3 rounded-2xl border border-black/[0.06] bg-lavender-2/60 px-4 py-3.5" data-testid={`aggregator-provider-value-${i}`}>
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-carza-dim text-carza"><v.icon size={15} /></span>
                      <div>
                        <h3 className="font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-nighttext">{v.title}</h3>
                        <p className="mt-1 text-[12.5px] leading-relaxed text-[#5c5470]">{v.desc}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
            <Reveal delay={0.15} className="lg:col-span-5">
              <div className="relative overflow-hidden rounded-3xl">
                <img src={IMAGES.partnerIndia} alt="A CARZA service partner" className="aspect-[4/5] w-full rounded-3xl object-cover" loading="lazy" data-testid="aggregator-provider-image" />
                <div className="absolute inset-0 rounded-3xl" style={{ background: "linear-gradient(180deg, transparent 45%, rgba(23,19,31,0.85))" }} />
                <Link to="/partners" className="btn btn-primary absolute bottom-5 left-5 right-5 py-3.5" data-testid="aggregator-provider-cta">
                  Become a CARZA Partner <ArrowRight size={15} />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

/* ---------- SECTION 7: CUSTOMER VALUE ---------- */
function CustomerValue({ onFind }) {
  return (
    <section className="container-x py-6" data-testid="aggregator-customer">
      <Reveal>
        <div className="rounded-[2rem] bg-white p-7 shadow-[0_20px_50px_-30px_rgba(23,19,31,0.25)] sm:p-10" data-testid="aggregator-customer-card">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-carza-dim text-carza"><HeartHandshake size={19} /></span>
            <h2 className="font-display text-2xl font-bold text-nighttext sm:text-3xl">Why Customers Choose CARZA</h2>
          </div>
          <div className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {CUSTOMER_VALUE.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.05}>
                <div className="flex h-full flex-col items-center gap-3 rounded-2xl border border-black/[0.06] bg-lavender-2/60 px-3 py-5 text-center" data-testid={`aggregator-customer-value-${i}`}>
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-carza-dim text-carza"><v.icon size={17} /></span>
                  <span className="text-[12px] font-semibold leading-snug text-nighttext">{v.title}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-8">
            <button onClick={onFind} className="btn btn-primary px-8 py-4" data-testid="aggregator-customer-cta">
              Find a Centre &amp; Book a Slot <ArrowRight size={16} />
            </button>
          </Reveal>
        </div>
      </Reveal>
    </section>
  );
}

/* ---------- SECTION 8: WATER ---------- */
function WaterEfficient() {
  const TRAD = ["Water", "Wash", "Rinse", "Dry"];
  const CARZA = ["Controlled Solution", "Clean", "Wipe", "Buff", "Quality Check"];
  return (
    <section className="container-x py-6" data-testid="aggregator-water">
      <Reveal>
        <div className="rounded-[2rem] bg-white p-7 shadow-[0_20px_50px_-30px_rgba(23,19,31,0.25)] sm:p-10" data-testid="aggregator-water-card">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-carza-dim text-carza"><Droplets size={19} /></span>
            <h2 className="font-display text-2xl font-bold text-nighttext sm:text-3xl">Smarter Car Care. Less Water.</h2>
          </div>
          <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-[#5c5470]">
            CARZA enables participating service providers to use controlled, water-efficient vehicle-cleaning processes designed to reduce dependence on conventional hose-and-rinse washing.
          </p>
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="rounded-3xl border border-black/[0.07] bg-lavender-2/50 p-6" data-testid="aggregator-water-traditional">
              <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-mist-2"><X size={11} /> Traditional process</p>
              <div className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-2 text-[13px] text-[#5c5470]">
                {TRAD.map((t, i) => (
                  <span key={t} className="flex items-center gap-2">
                    <span className="rounded-lg border border-black/[0.08] bg-white px-3 py-1.5">{t}</span>
                    {i < TRAD.length - 1 && <ArrowRight size={11} className="text-mist-2" />}
                  </span>
                ))}
              </div>
            </div>
            <div className="relative overflow-hidden rounded-3xl border border-carza/30 bg-gradient-to-br from-[#f3efff] to-white p-6" data-testid="aggregator-water-carza">
              <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-carza"><Check size={11} /> Carza process</p>
              <div className="mt-4 flex flex-wrap items-center gap-x-2 gap-y-2 text-[13px] font-medium text-nighttext">
                {CARZA.map((t, i) => (
                  <span key={t} className="flex items-center gap-2">
                    <span className={`rounded-lg border px-3 py-1.5 ${i === CARZA.length - 1 ? "border-carza/50 bg-carza text-white" : "border-carza/25 bg-carza-dim text-carza"}`}>{t}</span>
                    {i < CARZA.length - 1 && <ArrowRight size={11} className="text-carza" />}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-2.5">
            <span className="rounded-full border border-carza/30 bg-carza-dim px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-carza" data-testid="aggregator-water-badge">Water-Efficient Process</span>
            <span className="rounded-full border border-carza/30 bg-carza-dim px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-carza">Designed to Reduce Water Consumption</span>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

/* ---------- SECTION 9: ECOSYSTEM ---------- */
function Ecosystem() {
  return (
    <section className="container-x py-6" data-testid="aggregator-ecosystem">
      <Reveal>
        <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-[#241543] via-carza-deep to-[#1b0f33] p-7 sm:p-10" data-testid="aggregator-ecosystem-card">
          <div className="dot-grid pointer-events-none absolute inset-0 opacity-70" />
          <div className="relative grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <span className="eyebrow">The Carza Ecosystem</span>
              <h2 className="mt-4 font-display text-2xl font-bold text-paper sm:text-3xl">One Platform. Multiple Stakeholders.</h2>
              <div className="mt-7 flex flex-wrap gap-2">
                {ORBIT.map((o, i) => (
                  <span key={o.label} className="flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.05] px-3.5 py-2 text-[12px] font-medium text-paper" data-testid={`ecosystem-orbit-${i}`}>
                    <o.icon size={12} className="text-carza-lite" /> {o.label}
                  </span>
                ))}
              </div>
            </div>
            <div className="lg:col-span-7">
              <div className="flex flex-col">
                {ECOSYSTEM_CHAIN.map((n, i) => (
                  <div key={n.label}>
                    <motion.div whileHover={{ x: 6 }} className={`flex items-center gap-4 rounded-2xl border px-5 py-4 ${i === 1 ? "border-carza/50 bg-carza/[0.12]" : "border-white/10 bg-white/[0.04]"}`} data-testid={`ecosystem-node-${i}`}>
                      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${i === 1 ? "bg-carza text-white" : "bg-carza-dim text-carza-lite"}`}><n.icon size={17} /></span>
                      <span className="text-sm font-semibold text-paper">{n.label}</span>
                    </motion.div>
                    {i < ECOSYSTEM_CHAIN.length - 1 && <div className="flex justify-center py-1"><ArrowDown size={14} className="text-carza-lite/80" /></div>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

/* ---------- SECTION 10: SCALABILITY ---------- */
function Scalability() {
  const { config } = useApp();
  const ex = config.expansion;
  const markers = config.cities.map((c) => ({ name: c.name, live: c.live, pos: CITY_POS[c.name] || { x: 150, y: 120 } }));
  return (
    <section className="container-x py-6" data-testid="aggregator-scale">
      <Reveal>
        <div className="rounded-[2rem] bg-white p-7 shadow-[0_20px_50px_-30px_rgba(23,19,31,0.25)] sm:p-10" data-testid="aggregator-scale-card">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-carza-dim text-carza"><Globe2 size={19} /></span>
            <h2 className="font-display text-2xl font-bold text-nighttext sm:text-3xl">Built to Scale City by City.</h2>
          </div>
          <div className="mt-8 grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <p className="text-[15px] leading-relaxed text-[#5c5470]">
                Expanding a technology-enabled car-care network across India through a distributed service-provider model.
              </p>
              <div className="mt-6 flex flex-wrap gap-2" data-testid="aggregator-scale-pathway">
                {ex.pathway.map((p, i) => (
                  <span key={p} className="flex items-center gap-2">
                    <span className={`rounded-full border px-3.5 py-1.5 text-[12px] font-semibold ${i === 0 ? "border-carza/40 bg-carza-dim text-carza" : "border-black/[0.08] bg-lavender-2 text-nighttext"}`}>{p}</span>
                    {i < ex.pathway.length - 1 && <ArrowRight size={11} className="text-carza" />}
                  </span>
                ))}
              </div>
              <div className="mt-7 flex flex-col gap-2.5">
                <div className="flex items-center gap-3 rounded-xl border border-carza/25 bg-carza-dim/50 px-4 py-3" data-testid="scale-current">
                  <span className="h-2.5 w-2.5 rounded-full bg-carza" /><span className="text-[13px] font-semibold text-nighttext">Current Market</span>
                  <span className="ml-auto text-[12px] text-[#5c5470]">{ex.live_markets.join(" · ")}</span>
                </div>
                <div className="flex items-center gap-3 rounded-xl border border-dashed border-black/[0.14] px-4 py-3" data-testid="scale-target">
                  <span className="h-2.5 w-2.5 rounded-full border-2 border-dashed border-carza bg-transparent" /><span className="text-[13px] font-semibold text-nighttext">Target Market</span>
                  <span className="ml-auto text-[12px] text-[#5c5470]">{ex.target_markets.join(" · ")}</span>
                </div>
                <div className="flex items-center gap-3 rounded-xl border border-dashed border-black/[0.1] px-4 py-3" data-testid="scale-future">
                  <span className="h-2.5 w-2.5 rounded-full bg-black/[0.14]" /><span className="text-[13px] font-semibold text-nighttext">Future Expansion</span>
                  <span className="ml-auto text-[12px] text-[#5c5470]">{ex.future_markets.join(" · ")}</span>
                </div>
              </div>
            </div>
            <Reveal delay={0.1} className="lg:col-span-6">
              <div className="dot-grid-light mx-auto max-w-[380px] rounded-3xl border border-black/[0.06] bg-lavender-2/50 p-6" data-testid="aggregator-india-map">
                <svg viewBox="0 0 320 360" className="mx-auto h-[340px] w-full" role="img" aria-label="CARZA city expansion map of India">
                  <path
                    d="M96,18 L122,28 L140,50 L172,58 L212,64 L234,82 L248,78 L274,94 L294,110 L282,128 L258,118 L246,140 L238,170 L226,206 L212,240 L192,290 L179,312 L170,290 L152,250 L122,206 L96,166 L76,122 L58,112 L80,96 L62,76 L92,70 L72,50 Z"
                    fill="rgba(124,77,255,0.08)" stroke="rgba(109,61,245,0.4)" strokeWidth="1.4" strokeDasharray="4 5"
                  />
                  {markers.filter((m) => m.live && m.pos).map((m, i) => (
                    <g key={m.name}>
                      <circle cx={m.pos.x} cy={m.pos.y} r="5" fill="#6D3DF5" />
                      <circle cx={m.pos.x} cy={m.pos.y} r="5" fill="none" stroke="#6D3DF5" strokeWidth="1.5" className="ping-soft" style={{ transformOrigin: `${m.pos.x}px ${m.pos.y}px`, animationDelay: `${i * 0.7}s` }} />
                      <text x={m.pos.x + 11} y={m.pos.y + 4} fill="#17131F" fontSize="10" fontWeight="700" fontFamily="JetBrains Mono, monospace">{m.name}</text>
                    </g>
                  ))}
                  {markers.filter((m) => !m.live && m.pos).map((m) => (
                    <g key={m.name}>
                      <circle cx={m.pos.x} cy={m.pos.y} r="4.5" fill="#fff" stroke="#6D3DF5" strokeWidth="1.5" strokeDasharray="2 2" />
                      <text x={m.pos.x + 11} y={m.pos.y + 4} fill="#5c5470" fontSize="10" fontWeight="500" fontFamily="JetBrains Mono, monospace">{m.name}</text>
                    </g>
                  ))}
                </svg>
              </div>
            </Reveal>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

/* ---------- SECTION 11: INVESTOR VIEW ---------- */
function InvestorView() {
  return (
    <section className="container-x py-6" data-testid="aggregator-investor">
      <Reveal>
        <div className="rounded-[2rem] border border-carza/20 bg-white p-7 shadow-[0_20px_50px_-30px_rgba(109,61,245,0.35)] sm:p-10" data-testid="aggregator-investor-card">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-carza text-white"><TrendingUp size={19} /></span>
            <h2 className="font-display text-2xl font-bold text-nighttext sm:text-3xl">Building the Operating Layer for Car Care.</h2>
          </div>
          <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-[#5c5470]">
            CARZA Nearby is designed to combine customer demand, distributed service capacity, digital scheduling and standardized service processes into a scalable car-care network.
          </p>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {INVESTOR_PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.06}>
                <div className="flex h-full items-center gap-3.5 rounded-2xl border border-black/[0.06] bg-lavender-2/60 px-5 py-4" data-testid={`aggregator-investor-pillar-${i}`}>
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-carza-dim text-carza"><p.icon size={17} /></span>
                  <span className="text-[13px] font-bold leading-snug text-nighttext">{p.title}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-8">
            <Link to="/investors" className="btn btn-primary px-7 py-3.5" data-testid="aggregator-investor-cta">
              Explore Investor Overview <ArrowUpRight size={15} />
            </Link>
          </Reveal>
        </div>
      </Reveal>
    </section>
  );
}

/* ---------- SECTION 12: FINAL CTA ---------- */
function FinalCTA({ onFind }) {
  const { config } = useApp();
  return (
    <section className="container-x pt-6" data-testid="aggregator-final-cta">
      <Reveal>
        <div
          className="relative overflow-hidden rounded-[2rem] border border-carza/30 px-8 py-14 text-center sm:px-12"
          style={{ background: "linear-gradient(135deg, #6D3DF5 0%, #7C4DFF 45%, #9B7BFF 100%)" }}
        >
          <div className="dot-grid pointer-events-none absolute inset-0 opacity-40" />
          <div className="relative">
            <h2 className="mx-auto max-w-2xl font-display text-3xl font-bold tracking-tight text-white sm:text-5xl">Find a Car Wash Near You.</h2>
            <p className="mx-auto mt-4 max-w-md text-sm text-white/85 sm:text-base">Book a convenient slot and pay the washing centre directly after your wash.</p>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
              <button onClick={onFind} className="btn bg-white px-8 py-4 text-nighttext hover:bg-lavender" data-testid="aggregator-cta-book-btn">
                Find a Centre &amp; Book a Slot <ArrowRight size={16} />
              </button>
              <Link to="/partners" className="btn border border-white/50 px-8 py-4 text-white hover:bg-white/10" data-testid="aggregator-cta-partner-btn">
                Become a CARZA Partner
              </Link>
              <Link to="/for-business" className="btn border border-white/50 px-8 py-4 text-white hover:bg-white/10" data-testid="aggregator-cta-b2b-btn">
                Request a B2B Proposal
              </Link>
            </div>
          </div>
        </div>
      </Reveal>
      <p className="mt-6 text-center text-[12px] text-mist-2">
        Questions? <a href={waLink(config.brand.whatsapp, "Hi CARZA, I'd like to know more about CARZA Nearby.")} target="_blank" rel="noopener noreferrer" className="font-semibold text-carza underline" data-testid="aggregator-wa-link">Talk to the CARZA team</a>
      </p>
    </section>
  );
}
